"use client";

import { useState, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { Loader2 } from "lucide-react";
import { useAction } from "next-safe-action/hooks";
import { toast } from "sonner";
import { AuthShell } from "@/components/auth/auth-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/auth/password-input";
import { PasswordStrength } from "@/components/auth/password-strength";
import { registerUser } from "@/lib/actions/auth";

function RegisterContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const inviteToken = searchParams.get("invite");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordTouched, setPasswordTouched] = useState(false);

  const { execute, status, result } = useAction(registerUser, {
    onSuccess: async (data) => {
      if (data.data?.success) {
        // All users need to verify their email first
        // Invited users will be redirected to /invite/[token]/verify-complete after verification
        toast.success("Account created! Check your email to verify your account.");
        router.push(`/register/verify?email=${encodeURIComponent(email)}`);
      } else {
        toast.error(String(data.data?.error || "Registration failed"));
      }
    },
    onError: () => {
      toast.error("An error occurred during registration");
    },
  });

  const validateEmail = (value: string) => {
    if (!value) {
      setEmailError("Email is required");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setEmailError("Please enter a valid email address");
      return false;
    }
    setEmailError("");
    return true;
  };

  const handleEmailBlur = () => {
    validateEmail(email);
  };

  const validatePassword = (value: string): { valid: boolean; error?: string } => {
    if (value.length < 8) {
      return { valid: false, error: "Password must be at least 8 characters" };
    }
    if (!/[A-Z]/.test(value)) {
      return { valid: false, error: "Password must contain at least one uppercase letter" };
    }
    if (!/[a-z]/.test(value)) {
      return { valid: false, error: "Password must contain at least one lowercase letter" };
    }
    if (!/[0-9]/.test(value)) {
      return { valid: false, error: "Password must contain at least one number" };
    }
    return { valid: true };
  };

  const validateForm = () => {
    if (!name || !email || !password || !confirmPassword) {
      return false;
    }
    if (password !== confirmPassword) {
      return false;
    }
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.valid) {
      return false;
    }
    if (emailError) {
      return false;
    }
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setPasswordError("Passwords do not match");
      return;
    }

    const passwordValidation = validatePassword(password);
    if (!passwordValidation.valid) {
      setPasswordError(passwordValidation.error!);
      return;
    }

    setPasswordError("");
    execute({ name, email, password, inviteToken: inviteToken || undefined });
  };

  const isSubmitting = status === "executing";
  const isFormValid = validateForm();

  return (
    <AuthShell
      title="Create your account"
      subtitle="Join the NCSC CAF 4.0 assessment platform"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name field */}
        <div className="space-y-2">
          <Label htmlFor="name">Full name</Label>
          <Input
            id="name"
            name="name"
            type="text"
            placeholder="John Smith"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isSubmitting}
            autoComplete="name"
            required
          />
        </div>

        {/* Email field */}
        <div className="space-y-2">
          <Label htmlFor="email">Email address</Label>
          <Input
            id="email"
            name="username"
            type="email"
            placeholder="you@organisation.gov.uk"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) setEmailError("");
            }}
            onBlur={handleEmailBlur}
            disabled={isSubmitting}
            autoComplete="username"
            required
          />
          {emailError && (
            <p className="text-xs text-destructive">{emailError}</p>
          )}
        </div>

        {/* Password field */}
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <PasswordInput
            id="password"
            name="new-password"
            placeholder="Create a strong password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (passwordTouched && e.target.value) {
                const validation = validatePassword(e.target.value);
                setPasswordError(validation.valid ? "" : validation.error!);
              } else if (passwordError) {
                setPasswordError("");
              }
            }}
            onBlur={() => {
              setPasswordTouched(true);
              if (password) {
                const validation = validatePassword(password);
                setPasswordError(validation.valid ? "" : validation.error!);
              }
            }}
            disabled={isSubmitting}
            autoComplete="new-password"
            required
          />
          {passwordError && passwordTouched && (
            <p className="text-xs text-destructive">{passwordError}</p>
          )}
          {!passwordError && password && (
            <PasswordStrength password={password} />
          )}
          {!passwordTouched && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">Password must contain:</p>
              <ul className="text-xs text-muted-foreground space-y-0.5 ml-4">
                <li className={password.length >= 8 ? "text-green-600" : ""}>
                  {password.length >= 8 ? "✓" : "•"} At least 8 characters
                </li>
                <li className={/[A-Z]/.test(password) ? "text-green-600" : ""}>
                  {/[A-Z]/.test(password) ? "✓" : "•"} One uppercase letter
                </li>
                <li className={/[a-z]/.test(password) ? "text-green-600" : ""}>
                  {/[a-z]/.test(password) ? "✓" : "•"} One lowercase letter
                </li>
                <li className={/[0-9]/.test(password) ? "text-green-600" : ""}>
                  {/[0-9]/.test(password) ? "✓" : "•"} One number
                </li>
              </ul>
            </div>
          )}
        </div>

        {/* Confirm password field */}
        <div className="space-y-2">
          <Label htmlFor="confirmPassword">Confirm password</Label>
          <PasswordInput
            id="confirmPassword"
            name="new-password-confirm"
            placeholder="Re-enter your password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              if (passwordError) setPasswordError("");
            }}
            disabled={isSubmitting}
            autoComplete="new-password"
            required
          />
          {passwordError && (
            <p className="text-xs text-destructive">{passwordError}</p>
          )}
          {!passwordError && confirmPassword && password !== confirmPassword && (
            <p className="text-xs text-destructive">Passwords do not match</p>
          )}
        </div>

        {/* Submit button */}
        <Button
          type="submit"
          className="w-full h-10"
          disabled={!isFormValid || isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Creating account...
            </>
          ) : (
            "Create account"
          )}
        </Button>

        {/* Server error display */}
        {result.data && !result.data.success && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {String(result.data.error)}
          </div>
        )}

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">
              Already have an account?
            </span>
          </div>
        </div>

        {/* Sign in link */}
        <div className="text-center">
          <Link
            href="/login"
            className="text-sm text-muted-foreground hover:text-accent underline-offset-4 hover:underline transition-colors"
          >
            Sign in instead
          </Link>
        </div>
      </form>
    </AuthShell>
  );
}

export default function RegisterPage() {
  return (
    <Suspense fallback={
      <AuthShell
        title="Create your account"
        subtitle="Join the NCSC CAF 4.0 assessment platform"
      >
        <div className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AuthShell>
    }>
      <RegisterContent />
    </Suspense>
  );
}
